/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from "@tanstack/react-query"
import { queryOptions, useQuery } from "@tanstack/react-query"

import fetch from "~/backend/axiosClient"
import type {
  RequestConfig,
  ResponseErrorConfig,
  ResponseConfig,
} from "~/backend/axiosClient"

import type {
  FindAllEcoscoreDataSourcesQueryResponse,
  FindAllEcoscoreDataSourcesPathParams,
} from "../types/FindAllEcoscoreDataSources.ts"

export const findAllEcoscoreDataSourcesQueryKey = (
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"]
) =>
  [
    {
      url: "/v1/organization-settings/ecoscore-data-sources/:productSheetGroupId",
      params: { productSheetGroupId },
    },
  ] as const

export type FindAllEcoscoreDataSourcesQueryKey = ReturnType<
  typeof findAllEcoscoreDataSourcesQueryKey
>

/**
 * @description Returns a list of all distinct data source names from products matching the product sheet group filters. Environmental cost sources are extracted from product extra_info fields with the prefix 'environmental_cost_'. The prefix is removed from the response. The default 'ecobalyse' source is always included.
 * @summary Get all distinct ecoscore data sources for products in a product sheet group
 * {@link /v1/organization-settings/ecoscore-data-sources/:productSheetGroupId}
 */
export async function findAllEcoscoreDataSources(
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"],
  config: Partial<RequestConfig> & { client?: typeof fetch } = {}
) {
  const { client: request = fetch, ...requestConfig } = config

  const res = await request<
    FindAllEcoscoreDataSourcesQueryResponse,
    ResponseErrorConfig<Error>,
    unknown
  >({
    method: "GET",
    url: `/v1/organization-settings/ecoscore-data-sources/${productSheetGroupId}`,
    ...requestConfig,
  })
  return res
}

export function findAllEcoscoreDataSourcesQueryOptions(
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"],
  config: Partial<RequestConfig> & { client?: typeof fetch } = {}
) {
  const queryKey = findAllEcoscoreDataSourcesQueryKey(productSheetGroupId)
  return queryOptions<
    ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
    ResponseErrorConfig<Error>,
    ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
    typeof queryKey
  >({
    enabled: !!productSheetGroupId,
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return findAllEcoscoreDataSources(productSheetGroupId, config)
    },
  })
}

/**
 * @description Returns a list of all distinct data source names from products matching the product sheet group filters. Environmental cost sources are extracted from product extra_info fields with the prefix 'environmental_cost_'. The prefix is removed from the response. The default 'ecobalyse' source is always included.
 * @summary Get all distinct ecoscore data sources for products in a product sheet group
 * {@link /v1/organization-settings/ecoscore-data-sources/:productSheetGroupId}
 */
export function useFindAllEcoscoreDataSources<
  TData = ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
  TQueryData = ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
  TQueryKey extends QueryKey = FindAllEcoscoreDataSourcesQueryKey,
>(
  productSheetGroupId: FindAllEcoscoreDataSourcesPathParams["productSheetGroupId"],
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<FindAllEcoscoreDataSourcesQueryResponse>,
        ResponseErrorConfig<Error>,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<RequestConfig> & { client?: typeof fetch }
  } = {}
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...queryOptions } = queryConfig
  const queryKey =
    queryOptions?.queryKey ??
    findAllEcoscoreDataSourcesQueryKey(productSheetGroupId)

  const query = useQuery(
    {
      ...findAllEcoscoreDataSourcesQueryOptions(productSheetGroupId, config),
      queryKey,
      ...queryOptions,
    } as unknown as QueryObserverOptions,
    queryClient
  ) as UseQueryResult<TData, ResponseErrorConfig<Error>> & {
    queryKey: TQueryKey
  }

  query.queryKey = queryKey as TQueryKey

  return query
}
